/**
 * Network store — bridges Colyseus state into a Zustand store the React
 * tree can subscribe to. Connection logic lives in the World component;
 * this is just the data sink.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

/**
 * R232q5ah: per-class baseline HP/resource — used as a safety floor when
 * the schema field arrives as 0 or undefined (e.g. transient race during
 * onAdd before recomputePlayerStats has propagated, or a corrupted DB row).
 * Mirrors packages/aiwari-server/src/data/classes-server.ts SERVER_CLASSES.
 * Shared by realmClient / bgClient / dungeonClient upsertPlayer helpers so
 * the HUD never renders bogus "148/1" again.
 */
const CLASS_HP_BASELINE: Record<string, { hp: number; hpPerLevel: number; res: number; resPerLevel: number }> = {
  warrior: { hp: 200, hpPerLevel: 30, res: 100, resPerLevel: 0 },
  paladin: { hp: 190, hpPerLevel: 28, res: 110, resPerLevel: 4 },
  ranger:  { hp: 140, hpPerLevel: 18, res: 100, resPerLevel: 1 },
  rogue:   { hp: 130, hpPerLevel: 16, res: 100, resPerLevel: 1 },
  mage:    { hp: 110, hpPerLevel: 14, res: 200, resPerLevel: 8 },
  cleric:  { hp: 160, hpPerLevel: 22, res: 150, resPerLevel: 5 },
  shaman:  { hp: 165, hpPerLevel: 22, res: 160, resPerLevel: 6 },
  warlock: { hp: 115, hpPerLevel: 14, res: 200, resPerLevel: 8 },
  druid:   { hp: 155, hpPerLevel: 22, res: 150, resPerLevel: 5 },
};

/**
 * R232q5ah: returns hpMax to use, defending against the "148/1" bug by
 * replacing zero / undefined with a class+level derived baseline. The
 * server is authoritative — but during the brief window between onAdd
 * and the first recompute patch the field can arrive at the schema
 * default 0, which `?? 1` then mapped to a literal 1.
 */
export function safeHpMax(rawHpMax: unknown, classId: string, level: number): number {
  if (typeof rawHpMax === 'number' && rawHpMax > 0) return rawHpMax;
  const cls = CLASS_HP_BASELINE[classId] ?? CLASS_HP_BASELINE.warrior;
  return cls.hp + cls.hpPerLevel * Math.max(0, (level || 1) - 1);
}

export function safeResourceMax(rawResMax: unknown, classId: string, level: number): number {
  if (typeof rawResMax === 'number' && rawResMax > 0) return rawResMax;
  const cls = CLASS_HP_BASELINE[classId] ?? CLASS_HP_BASELINE.warrior;
  return cls.res + cls.resPerLevel * Math.max(0, (level || 1) - 1);
}

export interface RemotePlayer {
  id: string;
  name: string;
  side: string;
  classId: string;
  level: number;
  hp: number;
  hpMax: number;
  resource: number;
  resourceMax: number;
  pow: number;
  armor: number;
  xp: number;
  specId?: string;
  critPct?: number;
  hastePct?: number;
  masteryPct?: number;
  versPct?: number;
  pos: { x: number; y: number; z: number };
  heading: number;
  moving: boolean;
  zoneId?: string;
  realmKills?: number;
  realmDeaths?: number;
  realmPoints?: number;
  dead?: boolean;
  corpsePos?: { x: number; z: number };
  // R160: server state additions.
  buffs?: Array<{ id: string; endsAt: number }>;
  mounted?: boolean;
  mountId?: string;
  stormCrystals?: number;
  learnedAbilities?: string[];
  /** R232q44: WotLK numeric DBC spell ids unlocked by class+level
   *  (mirror of server PlayerState.knownSpells). Drives the «Заклинания»
   *  tab in Spellbook + DBC tooltips on hover. Empty until first replication. */
  knownSpells?: number[];
  /** R232q44: 6-slot glyph indices (Major 0-2, Minor 3-5; 0 = empty).
   *  Mirror of server PlayerState.glyphSlots. */
  glyphSlots?: number[];
  prevPos?: { x: number; y: number; z: number };
  prevUpdatedAt?: number;
  updatedAt?: number;
  /**
   * R232q20-Sirus-wire: id of the unit this player has explicitly targeted.
   * '' / undefined = no target. Drives Sirus TargetFrame for observers and
   * powers target-of-target lookup (read `players[targetId].targetId`).
   */
  targetId?: string;
  /**
   * R232q20-Sirus-wire: replicated aura list. Distinct from legacy `buffs`
   * (which keeps shipping for back-compat). Sirus BuffBar prefers this when
   * present and falls back to `buffs` otherwise.
   */
  auras?: Array<{
    id: string;
    icon: string;
    stacks: number;
    expiresAt: number;
    dispelType: string;
  }>;
  /**
   * R232q20-Sirus-wire: replicated cast bar for this player. Local caster
   * still drives their own CastBar from the `cast-start`/`cast-end` events
   * for low-latency feel; this field is what observers (TargetFrame, party
   * members' RaidFrame focus) use.
   */
  activeCast?: {
    abilityId: string;
    startTs: number;
    durationMs: number;
    channel?: boolean;
    interrupted?: boolean;
  };
}

export interface MobView {
  id: string;
  kind: string;
  level: number;
  hp: number;
  hpMax: number;
  pos: { x: number; y: number; z: number };
  heading: number;
  dead?: boolean;
  loot?: string[];
  despawnAt?: number;
  /**
   * R232q5ao: server aggro target (player session id, '' if unaggro'd).
   * Without this propagated to the client, World.tsx distance-cull at 60 m
   * disabled mob meshes that were chasing the player — the user reported
   * "мобы стоят на месте" because chasing mobs >60 m became invisible.
   */
  target?: string;
  // R232q5h T2.7: snapshot timing for client-side lerp interpolation.
  prevPos?: { x: number; y: number; z: number };
  prevUpdatedAt?: number; // performance.now() of the previous patch
  updatedAt?: number;     // performance.now() of the latest patch
  /** R232q5cw: server-polished RU name («Лесной волк»). */
  displayName?: string;
  /** R232q86zh6: WotLK CreatureDisplayInfo id — canonical model+skin key. */
  displayId?: number;
}

/**
 * R232q5aO (Wave 3a #7): Combat pet view — Hunter tame + Warlock summon.
 * Mirrors server PetState. Owner-only commands gated by selfId === ownerId.
 */
export interface PetView {
  id: string;
  ownerId: string;
  petType: string;
  displayName: string;
  family: number;
  level: number;
  xp: number;
  hp: number;
  hpMax: number;
  mana: number;
  manaMax: number;
  attackPower: number;
  armor: number;
  pos: { x: number; y: number; z: number };
  heading: number;
  stance: 'follow' | 'attack' | 'stay' | 'passive' | string;
  target: string;
  abilities: string[];
  cooldowns: Record<string, number>;
  modelRef: string;
  prevPos?: { x: number; y: number; z: number };
  prevUpdatedAt?: number;
  updatedAt?: number;
}

/** R232q5s: replicated NPC view (server NpcRegistry → state.npcs map). */
export interface NpcView {
  id: string;
  displayName: string;
  subtitle: string;
  npcType: string; // 'questGiver'|'vendor'|'trainer'|'innkeeper'|'flightmaster'|'repair'|'banker'
  faction: string;
  zoneId: string;
  modelRef: string;
  pos: { x: number; y: number; z: number };
  heading: number;
  questMarker?: string;
  lastEmote?: string;
  lastEmoteAt?: number;
  tags?: string;
}

interface NetStore {
  selfId: string | null;
  connected: boolean;
  ping: number;
  players: Record<string, RemotePlayer>;
  mobs: Record<string, MobView>;
  npcs: Record<string, NpcView>;
  /** R232q5aO: combat pets — Hunter / Warlock owned creatures. */
  pets: Record<string, PetView>;
  factionCounts: { tidal: number; rooted: number; anchorless: number };
  stormBonuses: { tidal: number; rooted: number; anchorless: number };
  /**
   * R232q20-Sirus-wire: group / raid composition replicated from server.
   * Map of playerId → groupId. Empty until the party-system pass lands.
   * GroupFrame / RaidFrame consume these via `derive` selectors below.
   */
  groupComposition: Record<string, string>;
  raidComposition: Record<string, string>;
  raidMode: boolean;
  setGroupComposition: (m: Record<string, string>) => void;
  setRaidComposition: (m: Record<string, string>) => void;
  setRaidMode: (v: boolean) => void;
  setSelf: (id: string | null) => void;
  setConnected: (c: boolean) => void;
  setPing: (p: number) => void;
  upsertPlayer: (p: RemotePlayer) => void;
  /**
   * R232q86zdb (Wave 2 #2): position-only fast path.
   *
   * Before this, every nested PositionSchema.x onChange triggered the full
   * `upsertPlayerFromState` -> upsertPlayer, which rebuilt 4 IIFE arrays
   * (buffs / learnedAbilities / knownSpells / glyphSlots) and sliced the
   * aura array. With 60 players × 15Hz that was ~24k array allocations/s.
   *
   * This method updates only pos / heading / moving and the matching
   * prevPos / updatedAt timestamps used by `interpolatedPos`. No buff/
   * aura rebuilds — those stay on the slow `upsertPlayer` path which the
   * parent onChange still calls for hp / buffs / spec / etc.
   */
  upsertPlayerPosition: (id: string, x: number, y: number, z: number, heading: number, moving?: boolean) => void;
  removePlayer: (id: string) => void;
  upsertMob: (m: MobView) => void;
  removeMob: (id: string) => void;
  upsertNpc: (n: NpcView) => void;
  removeNpc: (id: string) => void;
  upsertPet: (p: PetView) => void;
  removePet: (id: string) => void;
  setFactionCounts: (counts: { tidal: number; rooted: number; anchorless: number }) => void;
  setStormBonuses: (bonuses: { tidal: number; rooted: number; anchorless: number }) => void;
}

/**
 * R232q5h T2.7: smoothed display position for a remote entity.
 * Lerps from prevPos → pos based on interval (default 80ms send rate).
 * Renderers call this every frame to avoid the 30Hz "stairstep" look.
 */
export function interpolatedPos(entity: { pos: { x: number; y: number; z: number }; prevPos?: { x: number; y: number; z: number }; prevUpdatedAt?: number; updatedAt?: number }): { x: number; y: number; z: number } {
  const p = entity.pos;
  if (!entity.prevPos || !entity.updatedAt || !entity.prevUpdatedAt) return p;
  const span = Math.max(1, entity.updatedAt - entity.prevUpdatedAt);
  const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
  const elapsed = now - entity.updatedAt;
  // Slight render-side delay so we never extrapolate beyond the latest packet.
  // Targets a smoothed position one packet "behind" reality.
  const t = Math.min(1, Math.max(0, (elapsed + span) / (span * 2)));
  return {
    x: entity.prevPos.x + (p.x - entity.prevPos.x) * t,
    y: entity.prevPos.y + (p.y - entity.prevPos.y) * t,
    z: entity.prevPos.z + (p.z - entity.prevPos.z) * t,
  };
}

export const useNet = create<NetStore>((set) => ({
  selfId: null,
  connected: false,
  ping: 0,
  players: {},
  mobs: {},
  npcs: {},
  pets: {},
  factionCounts: { tidal: 0, rooted: 0, anchorless: 0 },
  stormBonuses: { tidal: 0, rooted: 0, anchorless: 0 },
  groupComposition: {},
  raidComposition: {},
  raidMode: false,
  setGroupComposition: (m) => set({ groupComposition: m }),
  setRaidComposition: (m) => set({ raidComposition: m }),
  setRaidMode: (v) => set({ raidMode: v }),
  setSelf: (id) => set({ selfId: id }),
  setConnected: (c) => set({ connected: c }),
  setPing: (p) => set({ ping: p }),
  upsertPlayer: (p) => set((s) => {
    // R232q5h T2.7: capture last patch as `prevPos` for interpolation.
    const prev = s.players[p.id];
    const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const merged: RemotePlayer = { ...p };
    if (prev && prev.pos) {
      (merged as RemotePlayer & { prevPos?: { x: number; y: number; z: number }; prevUpdatedAt?: number; updatedAt?: number }).prevPos = { ...prev.pos };
      (merged as RemotePlayer & { prevUpdatedAt?: number }).prevUpdatedAt = (prev as RemotePlayer & { updatedAt?: number }).updatedAt ?? now - 50;
    }
    (merged as RemotePlayer & { updatedAt?: number }).updatedAt = now;
    return { players: { ...s.players, [p.id]: merged } };
  }),
  // R232q86zdb (Wave 2 #2): position-only fast path.
  // We mutate the player row's `pos` in place rather than allocating a
  // brand-new RemotePlayer object — zustand still needs a fresh players
  // map for subscribers to fire, but the per-player object identity is
  // intentionally preserved so React.memo'd consumers that depend on the
  // whole row only re-render when something OTHER than pos changed (HP,
  // buffs, dead, etc.). Consumers that want to react to pos changes
  // (Minimap canvas, World scene) read via useNet.getState() each frame.
  upsertPlayerPosition: (id, x, y, z, heading, moving) => set((s) => {
    const prev = s.players[id];
    if (!prev) {
      // No row yet — fall back to a minimal stub so subsequent full
      // upsertPlayer merges cleanly. Server typically fires onAdd (full
      // upsert) before any nested onChange, so this branch is defensive.
      return s;
    }
    const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    // Stash the OLD pos as prevPos for the interpolator.
    prev.prevPos = { x: prev.pos.x, y: prev.pos.y, z: prev.pos.z };
    prev.prevUpdatedAt = (prev as RemotePlayer & { updatedAt?: number }).updatedAt ?? now - 50;
    // In-place pos mutation — same RemotePlayer identity. The PositionSchema
    // sub-object is preserved; we just refresh its x/y/z.
    prev.pos.x = x;
    prev.pos.y = y;
    prev.pos.z = z;
    prev.heading = heading;
    if (typeof moving === 'boolean') prev.moving = moving;
    (prev as RemotePlayer & { updatedAt?: number }).updatedAt = now;
    // Return a fresh players map so zustand subscribers fire — the row's
    // object identity is preserved so React.memo gating still works for
    // PlayerFrame/ActionBar (selfPlayer ref stays the same unless the
    // self-player row itself got mutated, which it has — but the narrow
    // useSelfHp/useSelfResource selectors return primitive tuples and
    // shallow-equal so they short-circuit anyway).
    return { players: { ...s.players, [id]: prev } };
  }),
  removePlayer: (id) =>
    set((s) => {
      const next = { ...s.players };
      delete next[id];
      return { players: next };
    }),
  upsertMob: (m) => set((s) => {
    const prev = s.mobs[m.id];
    const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const merged: MobView = { ...m };
    if (prev && prev.pos) {
      merged.prevPos = { ...prev.pos };
      merged.prevUpdatedAt = prev.updatedAt ?? now - 50;
    }
    merged.updatedAt = now;
    return { mobs: { ...s.mobs, [m.id]: merged } };
  }),
  removeMob: (id) =>
    set((s) => {
      const next = { ...s.mobs };
      delete next[id];
      return { mobs: next };
    }),
  upsertNpc: (n) => set((s) => ({ npcs: { ...s.npcs, [n.id]: n } })),
  removeNpc: (id) => set((s) => {
    const next = { ...s.npcs };
    delete next[id];
    return { npcs: next };
  }),
  upsertPet: (p) => set((s) => {
    const prev = s.pets[p.id];
    const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const merged: PetView = { ...p };
    if (prev && prev.pos) {
      merged.prevPos = { ...prev.pos };
      merged.prevUpdatedAt = prev.updatedAt ?? now - 50;
    }
    merged.updatedAt = now;
    return { pets: { ...s.pets, [p.id]: merged } };
  }),
  removePet: (id) => set((s) => {
    const next = { ...s.pets };
    delete next[id];
    return { pets: next };
  }),
  setFactionCounts: (counts) => set({ factionCounts: counts }),
  setStormBonuses: (bonuses) => set({ stormBonuses: bonuses }),
}));

// R232q86zb4: expose useNet on window для playwright/devtools diagnostics
// (read selfId, players[selfId].pos, etc. без обхода через React fiber).
if (typeof window !== 'undefined') {
  (window as unknown as { __aiwariNet?: typeof useNet }).__aiwariNet = useNet;
}

/* ────────────────────────────────────────────────────────────────────────
 * R232q86zdb (Wave 2 #1): split selectors.
 *
 * Before: every consumer subscribed via `useNet((s) => s.players)` which
 * returns the whole players map → React re-renders any subscriber on
 * EVERY player/state patch (~700/s with 60 players at 15 Hz).
 *
 * After: narrow per-field selectors below let React.memo'd components
 * subscribe to only the bytes they actually render. `usePlayerById(id)`
 * returns a stable reference to a single RemotePlayer; `useSelfHp()` /
 * `useSelfResource()` collapse to a tiny [cur, max] tuple so the bar
 * re-renders only when HP or MP actually changes.
 *
 * These are the canonical selectors that PlayerFrame / ActionBar / Bags
 * / Chat / Minimap consume. Direct `useNet((s) => s.players)` callers
 * still work but are gradually being replaced.
 *
 * Implementation note: zustand v5 dropped the 2-arg `useStore(selector,
 * equalityFn)` signature — for selectors that return a new array/object
 * each render we use `useShallow` from `zustand/react/shallow`. Selectors
 * that return primitives are auto-stable.
 * ──────────────────────────────────────────────────────────────────── */

export function usePlayerById(id: string | null | undefined): RemotePlayer | undefined {
  return useNet((s) => (id ? s.players[id] : undefined));
}

export function useSelfPlayer(): RemotePlayer | undefined {
  return useNet((s) => (s.selfId ? s.players[s.selfId] : undefined));
}

/** Returns `[hp, hpMax]` tuple — referentially stable when both values
 *  are unchanged. Used by HP bars so they don't re-render every pos patch. */
export function useSelfHp(): readonly [number, number] {
  return useNet(
    useShallow((s) => {
      const p = s.selfId ? s.players[s.selfId] : undefined;
      return [p?.hp ?? 0, p?.hpMax ?? 1] as const;
    }),
  );
}

/** Returns `[resource, resourceMax]` tuple — referentially stable. */
export function useSelfResource(): readonly [number, number] {
  return useNet(
    useShallow((s) => {
      const p = s.selfId ? s.players[s.selfId] : undefined;
      return [p?.resource ?? 0, p?.resourceMax ?? 1] as const;
    }),
  );
}

/** Self-only level — primitive, automatically referentially stable. */
export function useSelfLevel(): number {
  return useNet((s) => (s.selfId ? s.players[s.selfId]?.level ?? 1 : 1));
}

/** Self-only classId. */
export function useSelfClassId(): string | undefined {
  return useNet((s) => (s.selfId ? s.players[s.selfId]?.classId : undefined));
}

/** Self xp (primitive). */
export function useSelfXp(): number {
  return useNet((s) => (s.selfId ? s.players[s.selfId]?.xp ?? 0 : 0));
}

/** Self zone id (primitive). */
export function useSelfZoneId(): string | undefined {
  return useNet((s) => (s.selfId ? s.players[s.selfId]?.zoneId : undefined));
}

/** Self display name (primitive). */
export function useSelfName(): string | undefined {
  return useNet((s) => (s.selfId ? s.players[s.selfId]?.name : undefined));
}

/** Self dead flag (primitive). */
export function useSelfDead(): boolean {
  return useNet((s) => (s.selfId ? !!s.players[s.selfId]?.dead : false));
}
